import React, { useMemo, useRef } from "react";
import "./Forecast.css";

/**
 * Pure-React flow diagram:
 * - One very long horizontal arrow
 * - Alternating blurb cards (above/below the arrow)
 * - Dots on the arrow + connector lines to each card
 * - Optional thumbnail per step
 *
 * Images: place in /public/workflow/ and reference by filename in `image`.
 */

const coreInputs = [
  { type: "Elevation (DEM)", ext: ".asc", desc: "Digital elevation model (meters)" },
  { type: "Slope", ext: ".asc", desc: "Slope in degrees (derived from DEM)" },
  { type: "Aspect", ext: ".asc", desc: "Direction slope faces (0–360°)" },
  { type: "Fuel Model", ext: ".asc", desc: "Fuel model code (1–13 or 101–256 for LANDFIRE 2016)" },
  { type: "Canopy Cover", ext: ".asc", desc: "Percent cover (0–100)" },
  { type: "Canopy Height / Base Height / Bulk Density", ext: ".asc", desc: "Tree canopy structure (m, kg/m³)" },
];

const folderTree = [
  "EastBay_FlamMap/",
  "├── dem.asc",
  "├── slope.asc",
  "├── aspect.asc",
  "├── fuel_model.asc",
  "├── canopy_cover.asc",
  "├── canopy_base_height.asc",
  "├── canopy_height.asc",
  "├── canopy_bulk_density.asc",
  "│",
  "├── eastbay_weather.wtr",
  "├── eastbay_wind.wnd",
  "├── eastbay.adj",
  "├── EastBay.lcp",
  "│",
  "├── shapefile/",
  "│   ├── eastbay_boundary.shp",
  "│   ├── eastbay_boundary.dbf",
  "│   ├── eastbay_boundary.shx",
  "│",
  "└── metadata/",
  "    ├── readme.txt",
  "    └── data_sources.csv",
];

const steps = [
  {
    id: "overview",
    title: "what FlamMap needs!",
    blurb:
      "FlamMap simulates fire spread and behavior under constant weather and fuel moisture conditions. It needs six key raster inputs and optional weather/wind files.",
    image: "wildfirecartoon.jpg",
  },
  {
    id: "core",
    title: "Core Required Inputs",
    blurb: "Six rasters exported as ASCII Grid (.asc).",
    table: coreInputs,
    image: "fuels.jpg",
  },
  {
    id: "dataA",
    title: "A. Elevation, Slope, Aspect",
    blurb: "Use USGS 3DEP / EarthExplorer DEM, then derive Slope & Aspect.",
    bullets: [
      "Dataset: “USGS 1/3 arc-second DEM” (~10 m)",
      "Define AOI polygon around East Bay Hills",
      "Download DEM (.tif)",
      "Derive Slope & Aspect in QGIS/ArcGIS (Raster → Terrain Analysis)",
      "Export each as ASCII Grid (.asc)",
    ],
    image: "contour.png",
  },
  {
    id: "dataB",
    title: "B. Vegetation / Fuel Models (LANDFIRE)",
    blurb: "Download LANDFIRE products, clip, reproject, export",
    bullets: [
      "Products: fbfm40 (Surface Fuel), cc, cbh, ch, cbd",
      "Download clipped rasters for AOI (GeoTIFF)",
      "Reproject to DEM (align resolution/extent)",
      "Export each layer",
    ],
    image: "landfire.jpg",
  },
  {
    id: "dataC",
    title: "C. Weather & Wind Files",
    blurb: "Create .wtr and .wnd from NWS",
    bullets: [
      ".wtr: 1h, 10h, 100h, live herb, live woody (e.g., “6 7 8 100 120” %)",
      ".wnd: direction & speed (e.g., 270° @ 16 km/h typical West wind)",
      "Model-specific files (.adj/.fms/.lcp/.fpj/.fmp) are auto-generated",
    ],
    image: "wind.jpg",
  },
  {
    id: "dataD",
    title: "D. Optional Files",
    blurb: "AOI shapefiles and adjacency help with masking and topology.",
    bullets: [
      "We have a .geojson file for our AOI",
      ".adj generated by FlamMap",
      ".lcp is the compiled landscape used in simulations",
    ],
    image: "eastbayhillsfoggy.jpg",
  },
  {
    id: "folders",
    title: "Folder Structure to Replicate",
    pre: "Mirror this layout to keep inputs explicit and paths stable:",
    tree: folderTree,
  },
  {
    id: "gis",
    title: "Steps in QGIS / ArcGIS Pro",
    bullets: [
      "Clip rasters to same extent & resolution (Extraction → Clip by Mask, AOI shapefile)",
      "Ensure identical projection & cell alignment",
      "Export each as ASCII Grid (.asc): Right-click → Export → Save As → ASCII Grid",
    ],
    image: "gis.jpg",
  },
  {
    id: "flammap",
    title: "In FlamMap",
    bullets: ["Landscape → Build LCP", "Input DEM, Slope, Aspect, Fuel, Canopy layers", "Save as EastBay.lcp"],
    image: "flammapfont.jpg",
  },
  {
    id: "sanity",
    title: "IMPORTANT",
    bullets: [
      "All rasters share projection, cell size, and alignment",
      "Fuel model ranges ≤ 256; no missing data in slope/aspect/fuel",
      "Wind/weather align with study period & vegetation type",
    ],
    image: "mapprojection.jpg",
  },
  {
    id: "resources",
    title: "Recommended Resources",
    bullets: [
      "FlamMap User Guide – firelab.org",
      "LANDFIRE Viewer – landfire.gov/viewer",
      "USGS Downloader – apps.nationalmap.gov/downloader/",
      "YouTube: “Building a FlamMap Landscape File in ArcGIS” (~15 min)",
    ],
  },
];

/* … coreInputs, folderTree, steps (unchanged) … */

export default function Forecast() {
  // layout constants
  const stepWidth = 360;
  const laneHeight = 520;   // was 360; gives more room for bottom cards
  const arrowY = laneHeight / 2;


  const flowWidth = useMemo(() => steps.length * stepWidth + 180, [steps.length]);

  // ✅ define containerStyle
  const containerStyle = { width: `${flowWidth}px`, height: `${laneHeight}px` };

  // ✅ ref for the horizontal scroller
  const outerRef = useRef(null);

  // ✅ handler to pan the inner scroller to the step
  const goto = (e, id) => {
    e.preventDefault();
    const target = document.getElementById(id);
    const outer = outerRef.current;
    if (!target || !outer) return;

    const left = target.offsetLeft;
    const center = left - outer.clientWidth / 2 + 180;
    outer.scrollTo({ left: Math.max(0, center), behavior: "smooth" });

    // keep the hash in the URL (optional)
  };

  return (
    <div className="forecast-root">
      <header className="forecast-header">
        <div className="header-inner">
          <div>
            <h1>FlamMap</h1>
        <div className="header-sub">
            <p>One continuous flow: data sourcing → preprocessing → landscape build → checks → simulate</p>
            </div>
          </div>
        
          {/* ✅ use onClick to call goto */}
          <nav className="chip-nav">
            {steps.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="chip"
                onClick={(e) => goto(e, s.id)}
              >
                {s.title.split(".")[0]}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* ✅ attach the ref to the horizontal scroller */}
      <div className="flow-outer" ref={outerRef}>
        <div className="flow-inner" style={containerStyle}>
          {/* the long horizontal arrow (SVG) */}
          <svg
            className="flow-arrow"
            viewBox={`0 0 ${flowWidth} ${laneHeight}`}
            width={flowWidth}
            height={laneHeight}
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id="arrowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.5" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
              </linearGradient>
            </defs>
            <line
              x1="24"
              y1={arrowY}
              x2={flowWidth - 56}
              y2={arrowY}
              stroke="url(#arrowGrad)"
              strokeWidth="10"
              strokeLinecap="round"
            />
            <polygon
              points={`${flowWidth - 56},${arrowY - 16} ${flowWidth - 20},${arrowY} ${flowWidth - 56},${arrowY + 16}`}
              fill="currentColor"
            />
          </svg>

          {/* step cards */}
          {steps.map((step, i) => {
            const left = 80 + i * stepWidth;
            const isTop = i % 2 === 0; // alternate above/below
            const cardY = isTop ? 32 : arrowY + 36;
            const connectorY2 = isTop ? arrowY - 12 : arrowY + 12;

            return (
              <section id={step.id} key={step.id} className="step-wrap" style={{ left }}>
                <svg className="connector" width="1" height={laneHeight} viewBox={`0 0 1 ${laneHeight}`} preserveAspectRatio="none" aria-hidden>
                  <circle cx="0.5" cy={arrowY} r="5" className="dot" />
                  <line x1="0.5" y1={arrowY} x2="0.5" y2={connectorY2} className="connector-line" />
                </svg>

                <div className={`step-card ${isTop ? "top" : "bottom"}`} style={{ top: cardY }}>
                  <div className="step-title">
                    <h3>{step.title}</h3>
                    <span>#{i + 1}</span>
                  </div>

                  {step.blurb && <p className="blurb">{step.blurb}</p>}

                  {step.table && (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>File Type</th>
                            <th>Extension</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {step.table.map((r, idx) => (
                            <tr key={idx}>
                              <td>{r.type}</td>
                              <td>{r.ext}</td>
                              <td>{r.desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {step.pre && <p className="pre">{step.pre}</p>}

                  {step.tree && <pre className="folder-pre">{step.tree.join("\n")}</pre>}

                  {step.bullets && (
                    <ul className="bullets">
                      {step.bullets.map((b, idx) => <li key={idx}>{b}</li>)}
                    </ul>
                  )}

                  {step.tip && <div className="tip"><strong>Tip:</strong> {step.tip}</div>}

                  {step.image && (
                    <figure className="thumb">
                      <img src={`/workflow/${step.image}`} alt={step.title} loading="lazy" />
                      <figcaption>{step.image}</figcaption>
                    </figure>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
